import React, { useEffect, useRef, useState } from 'react';
import { SignaturePadModal } from './SignaturePadModal';
import { readSignatureImageFile, validateSignatureDataUrl } from '../utils/signatureImages';
import { processUploadedSignature, rasterizeTypedSignature } from '../utils/signatureProcessing';
import { SIGNATURE_FONTS, SIGNATURE_INK_COLORS } from '../constants/signing';
import { useDialogA11y } from '../utils/useDialogA11y';

type SignatureMode = 'draw' | 'type' | 'upload';

interface SignatureDraft {
  dataUrl: string;
  label: string;
}

interface UploadSource {
  dataUrl: string;
  label: string;
}

interface SignatureCreationFlowProps {
  mode: SignatureMode | null;
  profileName: string;
  onClose: () => void;
  onUseOnce: (dataUrl: string, label: string) => void;
  onSaveAndPlace: (dataUrl: string, label: string) => void;
  onError: (message: string) => void;
}

const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
  backgroundColor: '#fff',
};

export const SignatureCreationFlow: React.FC<SignatureCreationFlowProps> = ({
  mode,
  profileName,
  onClose,
  onUseOnce,
  onSaveAndPlace,
  onError,
}) => {
  const [draft, setDraft] = useState<SignatureDraft | null>(null);
  const [typedName, setTypedName] = useState(profileName);
  const [fontId, setFontId] = useState(SIGNATURE_FONTS[0].id);
  // H-2: ink color for the TYPE tab
  const [typedInkColor, setTypedInkColor] = useState(SIGNATURE_INK_COLORS[0].value);
  const [rasterizing, setRasterizing] = useState(false);
  const [uploadSource, setUploadSource] = useState<UploadSource | null>(null);
  const [removeBackground, setRemoveBackground] = useState(true);
  const [processedDataUrl, setProcessedDataUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uploadDialogRef = useRef<HTMLDivElement>(null);
  const typeDialogRef = useRef<HTMLDivElement>(null);

  const uploadModalOpen = mode === 'upload' || !!uploadSource;
  useDialogA11y(uploadModalOpen && !draft && mode === 'upload', onClose, uploadDialogRef);
  useDialogA11y(mode === 'type' && !draft, onClose, typeDialogRef);

  // Reset per-open state whenever the flow opens in a new mode or closes.
  useEffect(() => {
    setDraft(null);
    setUploadSource(null);
    setProcessedDataUrl(null);
    setRemoveBackground(true);
    setRasterizing(false);
    if (mode === 'type') {
      setTypedName(profileName);
    }
  }, [mode, profileName]);

  useEffect(() => {
    if (mode === 'upload') {
      const t = window.setTimeout(() => uploadInputRef.current?.click(), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [mode]);

  // Re-process the upload whenever the source or the background toggle changes.
  useEffect(() => {
    if (!uploadSource) {
      setProcessedDataUrl(null);
      return;
    }
    let cancelled = false;
    setProcessing(true);
    processUploadedSignature(uploadSource.dataUrl, { removeBackground })
      .then((dataUrl) => {
        if (!cancelled) setProcessedDataUrl(dataUrl);
      })
      .catch((err) => {
        if (!cancelled) {
          setUploadSource(null);
          onError(err instanceof Error ? err.message : 'Could not process signature image.');
        }
      })
      .finally(() => {
        if (!cancelled) setProcessing(false);
      });
    return () => {
      cancelled = true;
    };
    // onError comes from the parent as an inline closure; intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadSource, removeBackground]);

  // Paste an image from the clipboard into the upload pipeline while the flow is open.
  useEffect(() => {
    if (!mode) return;
    // H-5: allow webp/avif in paste, show friendly error for other image types
    const PASTE_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/avif'];
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          e.preventDefault();
          if (!PASTE_ALLOWED_TYPES.includes(item.type)) {
            onError('Paste a PNG, JPEG, or WebP image.');
            return;
          }
          const file = item.getAsFile();
          if (!file) continue;
          readSignatureImageFile(file)
            .then((dataUrl) => setUploadSource({ dataUrl, label: 'Pasted signature' }))
            .catch((err) =>
              onError(err instanceof Error ? err.message : 'Could not read pasted image.')
            );
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const confirmDraft = (dataUrl: string, label: string) => {
    const validation = validateSignatureDataUrl(dataUrl);
    if (!validation.ok) {
      onError(validation.message);
      return;
    }
    setDraft({ dataUrl, label });
  };

  const finishUseOnce = () => {
    if (!draft) return;
    onUseOnce(draft.dataUrl, draft.label);
    setUploadSource(null);
    setDraft(null);
    onClose();
  };

  const finishSaveAndPlace = () => {
    if (!draft) return;
    onSaveAndPlace(draft.dataUrl, draft.label);
    setUploadSource(null);
    setDraft(null);
    onClose();
  };

  if (!mode) return null;

  // A pasted image takes over the flow regardless of the originating tab,
  // so the upload pipeline (preview + background removal) renders first.
  if (mode === 'upload' || uploadSource) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        role="presentation"
        onClick={onClose}
      >
        <div
          ref={uploadDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-signature-title"
          tabIndex={-1}
          className="modal w-full max-w-md overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-2xl outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b px-5 py-4 dark:border-slate-700">
            <div id="upload-signature-title" className="text-base font-semibold text-slate-950 dark:text-slate-100">Upload signature</div>
            <div className="text-xs text-slate-500">Use a PNG, JPEG, or WebP image under 2 MB — or paste one with Ctrl+V.</div>
          </div>
          <div className="p-5">
            {/* H-5: include webp and avif in the accepted types */}
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/avif"
              className="hidden"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                try {
                  const dataUrl = await readSignatureImageFile(file);
                  setUploadSource({
                    dataUrl,
                    label: file.name.replace(/\.[^.]+$/, '') || 'Uploaded signature',
                  });
                } catch (err) {
                  onError(err instanceof Error ? err.message : 'Could not read signature image.');
                }
              }}
            />
            {!uploadSource ? (
              <button
                onClick={() => uploadInputRef.current?.click()}
                className="w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Choose PNG, JPEG, or WebP
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  Tip: you can also paste a copied image (Ctrl+V)
                </span>
              </button>
            ) : (
              <>
                <div
                  className="mb-3 flex min-h-[120px] items-center justify-center rounded-lg border p-3"
                  style={CHECKERBOARD_STYLE}
                >
                  {processing || !processedDataUrl ? (
                    <span className="rounded bg-white/80 px-2 py-1 text-xs text-slate-500">Processing image…</span>
                  ) : (
                    <img
                      src={processedDataUrl}
                      alt={uploadSource.label}
                      className="max-h-28 max-w-full object-contain"
                    />
                  )}
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={removeBackground}
                    onChange={(e) => setRemoveBackground(e.target.checked)}
                  />
                  Remove white background
                </label>
                <button
                  onClick={() => uploadInputRef.current?.click()}
                  className="mt-3 text-xs font-medium text-accent hover:underline"
                >
                  Choose a different image
                </button>
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t bg-slate-50 px-5 py-4">
            <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium">Cancel</button>
            {uploadSource && (
              <button
                onClick={() => {
                  if (!processedDataUrl) return;
                  confirmDraft(processedDataUrl, uploadSource.label);
                }}
                disabled={processing || !processedDataUrl}
                className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
              >
                Continue
              </button>
            )}
          </div>
          {draft && (
            <ChoiceDialog
              draft={draft}
              onCancel={() => setDraft(null)}
              onUseOnce={finishUseOnce}
              onSaveAndPlace={finishSaveAndPlace}
            />
          )}
        </div>
      </div>
    );
  }

  if (mode === 'draw') {
    return (
      <>
        {/* Fix M5: gate isOpen (and therefore the pad's useDialogA11y focus trap)
             on !draft so only one focus trap is active at a time. When a draft is
             present, the ChoiceDialog mounts its own trap; keeping the pad's trap
             armed simultaneously caused double-trap conflicts. The pad stays mounted
             (isOpen=false) to preserve the in-progress drawing while the choice
             dialog is shown — once the user dismisses the choice (setDraft(null))
             the pad's trap re-arms normally. */}
        <SignaturePadModal
          isOpen
          trapFocus={!draft}
          hidden={!!draft}
          title="Draw your signature"
          onClose={onClose}
          onSave={(dataUrl) => confirmDraft(dataUrl, 'Drawn signature')}
        />
        {draft && (
          <ChoiceDialog
            draft={draft}
            onCancel={() => setDraft(null)}
            onUseOnce={finishUseOnce}
            onSaveAndPlace={finishSaveAndPlace}
          />
        )}
      </>
    );
  }

  if (mode === 'type') {
    const selectedFont = SIGNATURE_FONTS.find((f) => f.id === fontId) ?? SIGNATURE_FONTS[0];
    const previewText = typedName.trim() || profileName || 'Signature';
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        role="presentation"
        onClick={onClose}
      >
        <div
          ref={typeDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="type-signature-title"
          tabIndex={-1}
          className="modal w-full max-w-lg overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-2xl outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b px-5 py-4 dark:border-slate-700">
            <div id="type-signature-title" className="text-base font-semibold text-slate-950 dark:text-slate-100">Type signature</div>
            <div className="text-xs text-slate-500">Create a reusable signature image from typed text.</div>
          </div>
          <div className="p-5">
            <input
              autoFocus
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Your full name"
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-2xl outline-none focus:border-accent"
              style={{ fontFamily: `"${selectedFont.family}", cursive` }}
            />
            <div className="mt-4 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Signature font">
              {SIGNATURE_FONTS.map((font) => (
                <button
                  key={font.id}
                  type="button"
                  role="radio"
                  aria-checked={fontId === font.id}
                  onClick={() => setFontId(font.id)}
                  className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                    fontId === font.id
                      ? 'border-accent bg-accent-muted'
                      : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <span
                    className="block truncate text-2xl leading-9"
                    style={{ fontFamily: `"${font.family}", cursive`, color: typedInkColor }}
                  >
                    {previewText}
                  </span>
                  <span className="block text-[11px] text-slate-400">{font.label}</span>
                </button>
              ))}
            </div>
            {/* H-2: ink color picker for typed signature */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-500">Ink color:</span>
              <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Typed signature ink color">
                {SIGNATURE_INK_COLORS.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    role="radio"
                    aria-checked={typedInkColor === color.value}
                    aria-label={color.label}
                    title={color.label}
                    onClick={() => setTypedInkColor(color.value)}
                    className="h-5 w-5 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      backgroundColor: color.value,
                      borderColor: typedInkColor === color.value ? 'var(--accent)' : '#e2e8f0',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t bg-slate-50 px-5 py-4">
            <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium">Cancel</button>
            <button
              disabled={rasterizing}
              onClick={async () => {
                const text = typedName.trim() || profileName || 'Signature';
                setRasterizing(true);
                try {
                  // H-2: pass the chosen ink color
                  const dataUrl = await rasterizeTypedSignature(text, selectedFont.family, typedInkColor);
                  confirmDraft(dataUrl, text);
                } catch (err) {
                  onError(err instanceof Error ? err.message : 'Could not render typed signature.');
                } finally {
                  setRasterizing(false);
                }
              }}
              className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
            >
              {rasterizing ? 'Rendering…' : 'Continue'}
            </button>
          </div>
          {draft && (
            <ChoiceDialog
              draft={draft}
              onCancel={() => setDraft(null)}
              onUseOnce={finishUseOnce}
              onSaveAndPlace={finishSaveAndPlace}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};

function ChoiceDialog({
  draft,
  onCancel,
  onUseOnce,
  onSaveAndPlace,
}: {
  draft: SignatureDraft;
  onCancel: () => void;
  onUseOnce: () => void;
  onSaveAndPlace: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(true, onCancel, dialogRef);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signature-choice-title"
        tabIndex={-1}
        className="w-full max-w-md overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-2xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-5 py-4 dark:border-slate-700">
          <div id="signature-choice-title" className="text-base font-semibold text-slate-950 dark:text-slate-100">How should this signature be used?</div>
          <div className="text-xs text-slate-500">Saving stores it only in this browser.</div>
        </div>
        <div className="p-5">
          <div className="mb-4 flex min-h-[88px] items-center justify-center rounded-lg border bg-slate-50 p-3">
            <img src={draft.dataUrl} alt={draft.label} className="max-h-20 max-w-full object-contain" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={onUseOnce} className="rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Use once
            </button>
            <button onClick={onSaveAndPlace} className="rounded-lg bg-state-success px-4 py-3 text-sm font-semibold text-white hover:bg-state-success">
              Save &amp; place
            </button>
          </div>
        </div>
        <div className="flex justify-end border-t bg-slate-50 px-5 py-3">
          <button onClick={onCancel} className="text-sm font-medium text-slate-500 hover:text-slate-800">Back</button>
        </div>
      </div>
    </div>
  );
}
